/**
 * AdminReviews.jsx
 * Admin dashboard page for managing Google-style text reviews.
 *
 * Features:
 *  - View all reviews (published + unpublished)
 *  - Add / Edit with Google-style live preview
 *  - "Show on pages" multi-checkbox (homepage + course slugs)
 *  - Star picker (1-5), Google Review link, display order
 *  - Publish / unpublish toggle
 *  - Delete with confirmation
 *  - "Seed 10 default reviews" button — populates MongoDB from hardcoded data
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../homepage/navbar/Navbar';
import { useThemeContext } from '../../context/theme-context';
import participantsData from '../internship/testimonials-internship/participantsData';
import './admin.css';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/$/, '');

// ── Avatar colours (matches public card) ───────────────────────────────────
const AVATAR_COLORS = ['#4285F4', '#EA4335', '#34A853', '#FBBC04', '#FF6D00', '#9C27B0'];
function avatarColor(name = '') {
  return AVATAR_COLORS[(name.charCodeAt(0) || 0) % AVATAR_COLORS.length];
}

// ── Inline Google G SVG ────────────────────────────────────────────────────
const GoogleLogo = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

// ── Stars display ───────────────────────────────────────────────────────────
const Stars = ({ rating = 5 }) => (
  <div style={{ display: 'flex', gap: '2px' }}>
    {[1, 2, 3, 4, 5].map(n => (
      <span key={n} style={{ color: n <= rating ? '#FBBC04' : '#dadce0', fontSize: '1rem' }}>
        {n <= rating ? '★' : '☆'}
      </span>
    ))}
  </div>
);

// ── Clickable star picker ───────────────────────────────────────────────────
const StarPicker = ({ value, onChange }) => (
  <div style={{ display: 'flex', gap: '4px', cursor: 'pointer' }}>
    {[1, 2, 3, 4, 5].map(n => (
      <span
        key={n}
        onClick={() => onChange(n)}
        style={{ fontSize: '1.5rem', color: n <= value ? '#FBBC04' : '#dadce0', userSelect: 'none' }}
      >★</span>
    ))}
  </div>
);

// ── Page selector (shared pattern, same as AdminVideos) ─────────────────────
const PageSelector = ({ selected = [], onChange, pages = [] }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const toggle = (val) => onChange(selected.includes(val) ? selected.filter(s => s !== val) : [...selected, val]);
  const label = selected.length === 0 ? 'All pages (no filter)'
    : selected.length === 1 ? pages.find(p => p.value === selected[0])?.label || selected[0]
    : `${selected.length} pages selected`;
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)} style={{
        padding: '0.5rem 0.8rem', borderRadius: '6px', border: '1px solid rgba(0,0,0,0.15)',
        background: 'var(--color-white)', fontSize: '0.85rem', cursor: 'pointer',
        width: '100%', textAlign: 'left', color: 'var(--color-dark)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem',
      }}>
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        <span style={{ flexShrink: 0 }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 100,
          background: 'var(--color-white)', border: '1px solid rgba(0,0,0,0.12)',
          borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          minWidth: '220px', maxHeight: '260px', overflowY: 'auto', padding: '0.5rem',
        }}>
          <div style={{ fontSize: '0.75rem', opacity: 0.5, padding: '0.25rem 0.5rem', marginBottom: '0.25rem' }}>
            Empty = show on all pages
          </div>
          {pages.map(p => (
            <label key={p.value} style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              padding: '0.35rem 0.5rem', borderRadius: '4px', cursor: 'pointer',
              fontSize: '0.85rem', color: 'var(--color-dark)',
            }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.04)'}
              onMouseLeave={e => e.currentTarget.style.background = ''}
            >
              <input type="checkbox" checked={selected.includes(p.value)} onChange={() => toggle(p.value)} style={{ cursor: 'pointer' }} />
              {p.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Google-style preview card ───────────────────────────────────────────────
const ReviewPreview = ({ review }) => {
  const name = review.name || 'Reviewer Name';
  const initial = name.charAt(0).toUpperCase();
  const bg = avatarColor(name);
  return (
    <div style={{ background: '#fff', border: '1px solid #e8eaed', borderRadius: '12px', padding: '1rem 1.2rem', boxShadow: '0 1px 4px rgba(60,64,67,0.15)', maxWidth: '340px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.4rem' }}>
        <div style={{ width: '2.2rem', height: '2.2rem', borderRadius: '50%', background: bg, color: '#fff', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.95rem' }}>{initial}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#202124' }}>{name}</div>
          <div style={{ fontSize: '0.7rem', color: '#70757a' }}>Completed the Program</div>
        </div>
        <GoogleLogo />
      </div>
      <Stars rating={review.rating ?? 5} />
      <p style={{ fontSize: '0.82rem', color: '#3c4043', lineHeight: 1.6, marginTop: '0.4rem', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 5, WebkitBoxOrient: 'vertical' }}>
        "{review.quote || 'Review text will appear here...'}"
      </p>
    </div>
  );
};

// ── Empty form ──────────────────────────────────────────────────────────────
const emptyForm = () => ({ name: '', quote: '', rating: 5, link: '', avatar: '', published: true, order: 0, pages: [] });

// ── Main component ──────────────────────────────────────────────────────────
const AdminReviews = () => {
  const { themeState } = useThemeContext();
  const navigate = useNavigate();

  const [reviews, setReviews] = useState([]);
  const [availablePages, setAvailablePages] = useState([{ value: 'homepage', label: '🏠 Homepage' }]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [showForm, setShowForm] = useState(false);
  const [showPreview, setShowPreview] = useState(true);

  const token = localStorage.getItem('token');
  const authHeader = { Authorization: token ? `Bearer ${token}` : '' };

  // ── Load available pages ────────────────────────────────────────────────
  const fetchPageOptions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/courses/admin/all`, {
        headers: authHeader,
        credentials: 'include'
      });
      const json = await res.json();
      if (res.ok && json.success && json.data) {
        const courseOptions = json.data.map(c => ({ value: c.slug, label: `📚 ${c.shortTitle || c.title}` }));
        setAvailablePages([{ value: 'homepage', label: '🏠 Homepage' }, ...courseOptions]);
      }
    } catch { /* Keep defaults */ }
  };

  // ── Fetch all reviews ───────────────────────────────────────────────────
  const fetchReviews = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/reviews/admin/all`, {
        headers: authHeader,
        credentials: 'include'
      });
      if (res.status === 401 || res.status === 403) { navigate('/login'); return; }
      const json = await res.json();
      if (res.ok && json.success) setReviews(json.data);
    } catch { setError('Failed to connect to backend.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchReviews(); fetchPageOptions(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Form helpers ────────────────────────────────────────────────────────
  const openAdd = () => { setEditingId(null); setForm(emptyForm()); setShowForm(true); setError(null); setSuccess(null); };

  const openEdit = (review) => {
    setEditingId(review._id);
    setForm({
      name: review.name || '', quote: review.quote || '',
      rating: review.rating ?? 5, link: review.link || '',
      avatar: review.avatar || '', published: review.published ?? true,
      order: review.order ?? 0, pages: review.pages || [],
    });
    setShowForm(true); setError(null); setSuccess(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelForm = () => { setShowForm(false); setEditingId(null); setForm(emptyForm()); setError(null); };

  // ── Save ────────────────────────────────────────────────────────────────
  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.quote.trim()) { setError('Reviewer name and review text are required.'); return; }
    try {
      setSaving(true); setError(null);
      const url = editingId ? `${API_URL}/api/reviews/${editingId}` : `${API_URL}/api/reviews`;
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...authHeader },
        credentials: 'include',
        body: JSON.stringify(form)
      });
      if (res.status === 401 || res.status === 403) { navigate('/login'); return; }
      const json = await res.json();
      if (res.ok && json.success) {
        setSuccess(editingId ? 'Review updated!' : 'Review added!');
        cancelForm(); fetchReviews(); setTimeout(() => setSuccess(null), 3000);
      } else { setError(json.message || 'Failed to save.'); }
    } catch { setError('Connection error.'); }
    finally { setSaving(false); }
  };


  // ── Delete ──────────────────────────────────────────────────────────────
  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete review by "${name}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/reviews/${id}`, {
        method: 'DELETE',
        headers: authHeader,
        credentials: 'include'
      });
      if (res.status === 401 || res.status === 403) { navigate('/login'); return; }
      const json = await res.json();
      if (res.ok && json.success) { setSuccess('Review deleted.'); fetchReviews(); setTimeout(() => setSuccess(null), 3000); }
    } catch { setError('Failed to delete.'); }
  };

  const handleTogglePublish = async (review) => {
    try {
      await fetch(`${API_URL}/api/reviews/${review._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        credentials: 'include',
        body: JSON.stringify({ ...review, published: !review.published })
      });
      fetchReviews();
    } catch { setError('Failed to update publish status.'); }
  };

  const inputStyle = {
    padding: '0.6rem 0.8rem', borderRadius: '6px', border: '1px solid rgba(0,0,0,0.12)',
    background: 'var(--color-white)', fontFamily: 'inherit', fontSize: '0.9rem',
    width: '100%', boxSizing: 'border-box', color: 'var(--color-dark)',
  };

  return (
    <main className={`${themeState.primary} ${themeState.background} admin__page`}>
      <Navbar />
      <section className="admin__container">
        <br />

        {/* ── Header ── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button className="btn sm" onClick={() => navigate('/admin')}>← Back</button>
            <h2 style={{ margin: 0 }}>Reviews Management</h2>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button className="btn primary" onClick={openAdd}>+ Add Review</button>
          </div>
        </div>

        {/* ── Status messages ── */}
        {error && <div className="admin__error">{error}</div>}
        {success && <div className="admin__error" style={{ background: 'rgba(76,175,80,0.12)', color: '#2e7d32' }}>{success}</div>}

        {/* ── Add / Edit Form ── */}
        {showForm && (
          <div style={{ background: 'var(--color-white)', border: '1px solid rgba(0,0,0,0.08)', borderRadius: '12px', padding: '1.5rem', marginBottom: '2rem', boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem' }}>{editingId ? 'Edit Review' : 'Add New Review'}</h3>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.85rem' }}>
                <input type="checkbox" checked={showPreview} onChange={e => setShowPreview(e.target.checked)} />
                Show preview
              </label>
            </div>
            <form onSubmit={handleSave}>
              <div style={{ display: 'grid', gridTemplateColumns: showPreview ? '1fr auto' : '1fr', gap: '2rem', alignItems: 'start' }}>
                {/* Form fields */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                  {/* Name */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <label style={{ fontWeight: 600, fontSize: '0.85rem' }}>Reviewer Name *</label>
                    <input style={inputStyle} placeholder="e.g. Joshua Olatunbosun" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
                  </div>
                  {/* Quote */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <label style={{ fontWeight: 600, fontSize: '0.85rem' }}>Review Text *</label>
                    <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: '100px' }} placeholder="What did they say about the program?" value={form.quote} onChange={e => setForm(p => ({ ...p, quote: e.target.value }))} rows={4} />
                  </div>
                  {/* Stars */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <label style={{ fontWeight: 600, fontSize: '0.85rem' }}>Star Rating</label>
                    <StarPicker value={form.rating} onChange={v => setForm(p => ({ ...p, rating: v }))} />
                  </div>
                  {/* Link */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <label style={{ fontWeight: 600, fontSize: '0.85rem' }}>Google Review Link (optional)</label>
                    <input style={inputStyle} placeholder="https://maps.app.goo.gl/..." value={form.link} onChange={e => setForm(p => ({ ...p, link: e.target.value }))} />
                  </div>
                  {/* Pages */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <label style={{ fontWeight: 600, fontSize: '0.85rem' }}>Show on Pages</label>
                    <PageSelector selected={form.pages} onChange={pages => setForm(p => ({ ...p, pages }))} pages={availablePages} />
                    <div style={{ fontSize: '0.75rem', opacity: 0.6, marginTop: '0.15rem' }}>Leave empty = show on all pages</div>
                  </div>
                  {/* Order + published */}
                  <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '1rem', alignItems: 'center' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      <label style={{ fontWeight: 600, fontSize: '0.85rem' }}>Display Order</label>
                      <input type="number" style={{ ...inputStyle, width: '100%' }} value={form.order} min={0} onChange={e => setForm(p => ({ ...p, order: Number(e.target.value) }))} />
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', paddingTop: '1.2rem' }}>
                      <input type="checkbox" checked={form.published} onChange={e => setForm(p => ({ ...p, published: e.target.checked }))} style={{ width: '1rem', height: '1rem' }} />
                      Published (visible on site)
                    </label>
                  </div>
                  {/* Actions */}
                  <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
                    <button type="submit" className="btn primary" disabled={saving}>{saving ? 'Saving...' : editingId ? 'Update Review' : 'Add Review'}</button>
                    <button type="button" className="btn" onClick={cancelForm}>Cancel</button>
                  </div>
                </div>
                {/* Live preview */}
                {showPreview && (
                  <div style={{ minWidth: '320px' }}>
                    <div style={{ fontSize: '0.8rem', opacity: 0.6, marginBottom: '0.5rem', fontWeight: 600 }}>PREVIEW</div>
                    <ReviewPreview review={form} />
                  </div>
                )}
              </div>
            </form>
          </div>
        )}

        {/* ── Reviews list ── */}
        {loading ? (
          <div className="admin__loading">Loading reviews...</div>
        ) : reviews.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', opacity: 0.5 }}>
            <p>No reviews yet.</p>
            <p style={{ fontSize: '0.85rem' }}>Add a review above to display it on the site.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {reviews.map(review => {
              return (
                <div key={review._id} style={{ background: 'var(--color-white)', border: '1px solid rgba(0,0,0,0.08)', borderRadius: '10px', padding: '1rem 1.25rem', display: 'grid', gridTemplateColumns: '2.2rem 1fr auto', gap: '1rem', alignItems: 'center', opacity: review.published ? 1 : 0.55 }}>
                  {/* Avatar */}
                  <div style={{ width: '2.2rem', height: '2.2rem', borderRadius: '50%', background: avatarColor(review.name), color: '#fff', fontWeight: 700, fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {(review.name || '?').charAt(0).toUpperCase()}
                  </div>
                  {/* Info */}
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>{review.name}</span>
                      <Stars rating={review.rating ?? 5} />
                      {!review.published && <span style={{ fontSize: '0.7rem', background: '#f5f5f5', border: '1px solid #ddd', borderRadius: '4px', padding: '0.1rem 0.4rem', color: '#777' }}>Unpublished</span>}
                    </div>
                    <p style={{ margin: '0.15rem 0 0', fontSize: '0.83rem', color: 'var(--color-dark)', opacity: 0.75, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '500px' }}>
                      "{review.quote}"
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.25rem' }}>
                      <span style={{ fontSize: '0.75rem', color: 'var(--color-dark)', opacity: 0.6 }}>Show on:</span>
                      <div style={{ width: '180px' }}>
                        <PageSelector
                          selected={review.pages || []}
                          onChange={async (newPages) => {
                            setReviews(prev => prev.map(r => r._id === review._id ? { ...r, pages: newPages } : r));
                            try {
                              const token = localStorage.getItem('token');
                              await fetch(`${API_URL}/api/reviews/${review._id}`, {
                                method: 'PUT',
                                credentials: 'include',
                                headers: {
                                  'Content-Type': 'application/json',
                                  Authorization: token ? `Bearer ${token}` : '',
                                },
                                body: JSON.stringify({ ...review, pages: newPages }),
                              });
                            } catch (err) {
                              console.error("Failed to update review pages inline:", err);
                            }
                          }}
                          pages={availablePages}
                        />
                      </div>
                    </div>
                  </div>
                  {/* Actions */}
                  <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <button className="btn sm" onClick={() => handleTogglePublish(review)} style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}>{review.published ? '👁 Hide' : '👁 Show'}</button>
                    <button className="btn sm primary" onClick={() => openEdit(review)} style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}>Edit</button>
                    <button className="btn sm" onClick={() => handleDelete(review._id, review.name)} style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem', background: 'rgba(211,47,47,0.1)', color: '#c62828' }}>Delete</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Info note ── */}
        <div style={{ marginTop: '2rem', padding: '1rem 1.25rem', background: 'rgba(66,133,244,0.06)', border: '1px solid rgba(66,133,244,0.2)', borderRadius: '8px', fontSize: '0.85rem', color: 'var(--color-dark)', opacity: 0.8 }}>
          <strong>ℹ️ Fallback note:</strong> If no published reviews exist in the DB, the site automatically shows the 10 hardcoded static reviews. Use "🌱 Seed Default Reviews" to copy them here and gain full control.
        </div>

        <br /><br />
      </section>
    </main>
  );
};

export default AdminReviews;
